import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Textarea,
  useTheme,
  VStack,
} from '@chakra-ui/react';
import React, {useMemo, useState} from 'react';
import Select, {CSSObjectWithLabel, SingleValue} from 'react-select';
import dayjs from 'dayjs';
import {BlueButton, WhiteButton} from '../../button';
import {TextG10, TextG12, TextG14, TextG16} from '../../typography';
import {useCancellationReasons} from '../../../hooks/useCancellationReasons';
import {CancellationReason} from '../../../types/membership';

const OTHER_REASON_ID = 4;
const MAX_COMMENT_LENGTH = 1000;

type SelectOption = {
  value: number;
  label: string;
};

type CancelMembershipModalProps = {
  isOpen: boolean;
  onClose: () => void;
  membershipPlanName: string;
  paidThroughDate?: string;
  onConfirm: (reasonId: number, reasonComment?: string) => void;
  isLoading?: boolean;
};

const CancelMembershipModal = ({
  isOpen,
  onClose,
  membershipPlanName,
  paidThroughDate,
  onConfirm,
  isLoading = false,
}: CancelMembershipModalProps) => {
  const theme = useTheme();
  const [selectedReason, setSelectedReason] = useState<SelectOption | null>(null);
  const [reasonComment, setReasonComment] = useState('');

  const {data: cancellationReasons, isLoading: isLoadingReasons} = useCancellationReasons(isOpen);

  const options: SelectOption[] = useMemo(() => {
    if (!cancellationReasons) {
      return [];
    }
    return cancellationReasons.map((reason: CancellationReason) => ({
      value: reason.id,
      label: reason.label,
    }));
  }, [cancellationReasons]);

  const isOtherSelected = selectedReason?.value === OTHER_REASON_ID;
  const isCommentRequired = isOtherSelected;
  const isSubmitDisabled =
    !selectedReason || (isCommentRequired && !reasonComment.trim()) || isLoading;

  const handleConfirm = () => {
    if (!selectedReason) {
      return;
    }
    const comment = reasonComment.trim() || undefined;
    onConfirm(selectedReason.value, comment);
  };

  const handleClose = () => {
    setSelectedReason(null);
    setReasonComment('');
    onClose();
  };

  const handleReasonChange = (newValue: SingleValue<SelectOption>) => {
    setSelectedReason(newValue);
  };

  const selectStyles: any = {
    control: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      height: 'auto',
      width: '100%',
      padding: '2px 8px',
      fontFamily: 'Gordita',
      fontSize: '14px',
      fontWeight: '400',
      borderRadius: '8px',
      background: 'white',
      border: '1px solid #E5E5E5',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#E5E5E5',
      },
    }),
    placeholder: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      color: theme.colors.lightestGrey,
      fontSize: '14px',
    }),
    menu: (baseStyles: CSSObjectWithLabel) => ({
      ...baseStyles,
      zIndex: 10,
    }),
    option: (baseStyles: CSSObjectWithLabel, state: {isSelected: boolean}) => ({
      ...baseStyles,
      fontFamily: 'Gordita',
      fontSize: '14px',
      backgroundColor: state.isSelected ? theme.colors.blue : 'white',
      color: state.isSelected ? 'white' : '#333',
      '&:hover': {
        backgroundColor: state.isSelected ? theme.colors.blue : '#f5f5f5',
      },
    }),
  };

  const descriptionText = `Are you sure you want to cancel your ${membershipPlanName} membership? You will still have access to your ${
    paidThroughDate
      ? 'paid membership until ' + dayjs(paidThroughDate).format('MMMM D, YYYY')
      : 'free account'
  }.`;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent w="90%" px={4} pt={6} pb={4} h="auto" maxH="90vh" overflowY="auto">
        <ModalCloseButton background="transparent" top={0} right={0} />
        <ModalBody p={0} textAlign="center">
          <VStack spacing={4} align="stretch">
            <TextG16 fontWeight="500">Cancel {membershipPlanName} Membership?</TextG16>
            <TextG12 whiteSpace="pre-line">{descriptionText}</TextG12>

            <Box textAlign="left">
              <TextG14 mb={2} fontWeight="500">
                Why are you cancelling?{' '}
                <Box as="span" color="red">
                  *
                </Box>
              </TextG14>
              <Select
                value={selectedReason}
                onChange={handleReasonChange}
                options={options}
                placeholder="Select a reason..."
                isSearchable={false}
                isLoading={isLoadingReasons}
                styles={selectStyles}
                classNamePrefix="cancel-reason-select"
              />
            </Box>

            <Box textAlign="left">
              <TextG14 mb={2} fontWeight="500">
                Additional comments{isCommentRequired ? ' ' : ' (optional)'}
                {isCommentRequired && (
                  <Box as="span" color="red">
                    *
                  </Box>
                )}
              </TextG14>
              <Textarea
                value={reasonComment}
                onChange={(e) =>
                  e.target.value.length <= MAX_COMMENT_LENGTH && setReasonComment(e.target.value)
                }
                placeholder={
                  isCommentRequired
                    ? 'Please tell us more about why you are cancelling...'
                    : 'Any additional feedback? (optional)'
                }
                resize="none"
                minH="80px"
                fontSize="14px"
                border="1px solid #E5E5E5"
                _focus={{borderColor: theme.colors.blue, boxShadow: 'none'}}
              />
              <TextG10 w="100%" textAlign="right" mt="4px" color={theme.colors.lighterGrey}>
                {reasonComment.length}/{MAX_COMMENT_LENGTH}
              </TextG10>
            </Box>

            <Flex justifyContent="space-evenly" mt={2}>
              <WhiteButton
                h="30px"
                w="160px"
                minW="100px"
                borderRadius="19px"
                color="#757D84"
                border="1px solid #757D84"
                onClick={handleClose}>
                <TextG10 py={3}>No, Keep Membership</TextG10>
              </WhiteButton>
              <BlueButton
                h="30px"
                w="160px"
                minW="100px"
                borderRadius="19px"
                onClick={handleConfirm}
                isDisabled={isSubmitDisabled}
                opacity={isSubmitDisabled ? 0.5 : 1}>
                <TextG10 py={3}>{isLoading ? 'Cancelling...' : 'Yes, Cancel'}</TextG10>
              </BlueButton>
            </Flex>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CancelMembershipModal;
